// Shared constants for the Market & Value Chain module.

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -------- Value Chain Canvas: 6 stages --------
export const STAGES = [
  { id: "rd",                name: "R&D",                  what: "Discovery and pre-clinical research — where the science becomes an asset." },
  { id: "clinical_dev",      name: "Clinical Development", what: "Clinical trials — where the asset is tested in humans." },
  { id: "regulatory",        name: "Regulatory",           what: "Regulatory review and approval — where the asset earns the right to reach patients." },
  { id: "manufacturing",     name: "Manufacturing",        what: "CMC, supply chain and scale-up — where the asset becomes a product." },
  { id: "commercialisation", name: "Commercialisation",    what: "Launch, marketing, sales, market access — where the product reaches the market." },
  { id: "patient_support",   name: "Patient Support",      what: "Adherence, education, services — where the product delivers value to patients." },
];
export const STAGE_IDS = STAGES.map((s) => s.id);

// -------- Value Chain: 9 card labels --------
export const TAGS = [
  { id: "competitor",         label: "Competitor",         chip: "bg-red-50 border-red-300 text-red-700",         dot: "bg-red-500" },
  { id: "future_threat",      label: "Future threat",      chip: "bg-rose-50 border-rose-300 text-rose-700",      dot: "bg-rose-500" },
  { id: "potential_partner",  label: "Potential partner",  chip: "bg-purple-50 border-purple-300 text-purple-700",dot: "bg-purple-500" },
  { id: "potential_customer", label: "Potential customer",chip: "bg-teal-50 border-teal-300 text-teal-700",       dot: "bg-teal-500" },
  { id: "enabler",            label: "Enabler",            chip: "bg-green-50 border-green-300 text-green-700",   dot: "bg-green-500" },
  { id: "blocker",            label: "Blocker",            chip: "bg-orange-50 border-orange-300 text-orange-700",dot: "bg-orange-500" },
  { id: "gatekeeper",         label: "Gatekeeper",         chip: "bg-amber-50 border-amber-300 text-amber-700",   dot: "bg-amber-500" },
  { id: "payer",              label: "Payer",              chip: "bg-blue-50 border-blue-300 text-blue-700",      dot: "bg-blue-500" },
  { id: "kol",                label: "KOL",                chip: "bg-violet-50 border-violet-300 text-violet-700",dot: "bg-violet-500" },
];
export const tagMeta = (id) => TAGS.find((t) => t.id === id) || TAGS[0];

// -------- News category chip styles --------
export const NEWS_CAT_STYLE = {
  competitor:    "bg-red-50 text-red-700 border-red-200",
  regulatory:    "bg-amber-50 text-amber-700 border-amber-200",
  deal:          "bg-purple-50 text-purple-700 border-purple-200",
  manufacturing: "bg-orange-50 text-orange-700 border-orange-200",
  payer:         "bg-blue-50 text-blue-700 border-blue-200",
  kol:           "bg-violet-50 text-violet-700 border-violet-200",
  platform:      "bg-teal-50 text-teal-700 border-teal-200",
  other:         "bg-slate-50 text-slate-600 border-slate-200",
};

// -------- Competitor Landscape --------
export const COMPETITOR_ZONES = [
  { id: "current",  label: "Current alternatives", hint: "Current standards of care, older approved therapies, non-drug options." },
  { id: "direct",   label: "Direct competitors",   hint: "Same target/MoA or same modality attacking the same indication." },
  { id: "indirect", label: "Indirect competitors", hint: "Different mechanism but solving the same patient problem." },
  { id: "future",   label: "Future threats",       hint: "Emerging assets, new modalities, fast-followers, large-pharma or China plays." },
];
export const COMPETITOR_HORIZONS = [
  { id: "now", label: "Now" },
  { id: "3y",  label: "3 years" },
  { id: "5y",  label: "5 years" },
  { id: "10y", label: "10 years" },
];
export const THREAT_STYLE = {
  high:   "bg-red-100 text-red-700 border-red-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  low:    "bg-slate-100 text-slate-600 border-slate-300",
};
export const HORIZON_STYLE = {
  now: "bg-teal-50 text-teal-700 border-teal-200",
  "3y": "bg-blue-50 text-blue-700 border-blue-200",
  "5y": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "10y": "bg-violet-50 text-violet-700 border-violet-200",
};
export const COMPETITOR_FRAMEWORK = [
  "Who is solving the same patient problem?",
  "Who is working on the same target or pathway?",
  "Who is using a different modality to solve the same problem?",
  "Who could become best-in-class before you get there?",
  "Which failed assets should you learn from?",
  "Which emerging assets could change the standard of care?",
  "Could China / US / large pharma / fast followers change this landscape?",
];

// -------- Ecosystem Questions: stakeholder categories --------
export const STAKEHOLDERS = [
  { id: "pharma_search_eval",  label: "Pharma Search & Evaluation" },
  { id: "pharma_bd",           label: "Pharma BD / Transactions" },
  { id: "pharma_clinical_dev", label: "Pharma Clinical Development" },
  { id: "pharma_commercial",   label: "Pharma Commercial / Lifecycle" },
  { id: "clinical_kol",        label: "Clinical KOLs" },
  { id: "trial_investigator",  label: "Trial investigators" },
  { id: "nhs_adoption",        label: "NHS / adoption stakeholders" },
  { id: "payer_hta",           label: "Payers / NICE / HTA" },
  { id: "patient_group",       label: "Patient groups" },
  { id: "cro",                 label: "CROs" },
  { id: "cdmo",                label: "CDMOs" },
  { id: "regulatory_advisor",  label: "Regulatory advisors" },
  { id: "investor",            label: "Investors" },
  { id: "acquirer",            label: "Potential acquirers" },
];
export const stakeholderMeta = (id) => STAKEHOLDERS.find((s) => s.id === id);

// -------- Helpers --------
export const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const safeHostname = (url) => {
  try { return new URL(url).hostname.replace("www.", ""); } catch { return ""; }
};

export const emptyStage = () => ({ create_value: "", assumptions: "", items: [] });

export const normaliseItem = (it) => {
  if (!it) return null;
  let tags = Array.isArray(it.tags) ? it.tags : (it.tag ? [it.tag] : []);
  tags = tags.filter((t) => TAGS.find((x) => x.id === t));
  return { id: it.id || uid(), text: it.text || "", note: it.note || "", tags };
};
